import { Controller } from '@hotwired/stimulus';
import * as pdfjsLib from 'pdfjs-dist';

// Renders a PDF (from its `src` value) as a vertical stack of canvases inside a
// scrollable container. Unlike <embed>/<iframe>, canvas rendering is reliable
// and scrollable on iOS Safari, which only displays the first page of an
// embedded PDF. Pages are rasterized at the container width so the contract
// stays readable on mobile, and re-rendered when that width changes.
const TRANSLATIONS = {
  loading: {
    fr: 'Chargement du contrat…',
    en: 'Loading the contract…',
    es: 'Cargando el contrato…',
  },
  fallback: {
    fr: 'Consulter le contrat dans un nouvel onglet',
    en: 'Open the contract in a new tab',
    es: 'Abrir el contrato en una nueva pestaña',
  },
};

const RESIZE_DEBOUNCE_MS = 150;

export default class PdfViewerController extends Controller {
  static values = {src: String}

  connect() {
    this.locale = window.getCurrentLocale?.() || 'fr';
    this.renderTasks = [];
    this.lastRenderedWidth = 0;
    this.resizeTimeout = null;

    this.#configureWorker();
    this.#showLoading();
    this.#loadDocument();

    this.resizeObserver = new ResizeObserver(() => this.#scheduleRender());
    this.resizeObserver.observe(this.element);
  }

  disconnect() {
    this.resizeObserver?.disconnect();
    clearTimeout(this.resizeTimeout);
    this.#cancelRenderTasks();
    this.pdfDocument?.destroy();
  }

  #configureWorker() {
    const workerSrc = document.querySelector('meta[name="pdf-worker-src"]')?.content;

    if (workerSrc) pdfjsLib.GlobalWorkerOptions.workerSrc = workerSrc;
  }

  #loadDocument() {
    pdfjsLib.getDocument(this.srcValue).promise
      .then((pdfDocument) => {
        this.pdfDocument = pdfDocument;
        this.#renderAllPages();
      })
      .catch(() => this.#showFallback());
  }

  #scheduleRender() {
    clearTimeout(this.resizeTimeout);
    this.resizeTimeout = setTimeout(() => this.#renderAllPages(), RESIZE_DEBOUNCE_MS);
  }

  #renderAllPages() {
    if (!this.pdfDocument) return;

    const width = this.element.clientWidth;
    // Skip while hidden (d-none → width 0) or when the width has not changed
    // (appending canvases grows the height and re-triggers the observer).
    if (width === 0 || width === this.lastRenderedWidth) return;

    this.lastRenderedWidth = width;
    this.#cancelRenderTasks();
    this.#clear();

    const devicePixelRatio = window.devicePixelRatio || 1;
    const pageNumbers = Array.from({length: this.pdfDocument.numPages}, (_, index) => index + 1);

    pageNumbers.forEach((pageNumber) => {
      // Append the canvas synchronously so pages keep their order even though
      // getPage resolves asynchronously.
      const canvas = document.createElement('canvas');
      canvas.className = 'pdf-viewer-page';
      this.element.append(canvas);

      this.pdfDocument.getPage(pageNumber).then((page) => {
        const baseViewport = page.getViewport({scale: 1});
        const viewport = page.getViewport({scale: width / baseViewport.width});

        canvas.width = Math.floor(viewport.width * devicePixelRatio);
        canvas.height = Math.floor(viewport.height * devicePixelRatio);

        const renderTask = page.render({
          canvasContext: canvas.getContext('2d'),
          viewport,
          transform: devicePixelRatio === 1 ? null : [devicePixelRatio, 0, 0, devicePixelRatio, 0, 0],
        });
        this.renderTasks.push(renderTask);
      });
    });
  }

  #cancelRenderTasks() {
    this.renderTasks.forEach((renderTask) => renderTask.cancel());
    this.renderTasks = [];
  }

  #clear() {
    while (this.element.firstChild) {
      this.element.removeChild(this.element.firstChild);
    }
  }

  #showLoading() {
    this.#clear();

    const paragraph = document.createElement('p');
    paragraph.className = 'pdf-viewer-message';
    paragraph.textContent = TRANSLATIONS.loading[this.locale];
    this.element.append(paragraph);
  }

  #showFallback() {
    this.#clear();

    const link = document.createElement('a');
    link.className = 'pdf-viewer-fallback';
    link.href = this.srcValue;
    link.target = '_blank';
    link.rel = 'noopener';
    link.textContent = TRANSLATIONS.fallback[this.locale];
    this.element.append(link);
  }
}
